import { useMemo, useState } from 'react';
import { Network as NetworkIcon, Search, Users, Phone, Mail, Star } from 'lucide-react';
import { useNetwork } from '@/hooks/useDashboard';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { NetworkBucket, NetworkTarget } from '@/lib/types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SECTION_LABELS: Record<string, string> = {
  targets: 'Cibles',
  admins: 'Admins',
  overlaps_clients: 'Clients déjà connus',
  overlaps_clients_crm: 'Recoupements CRM',
  ponts_win_ltpf: 'Ponts WIN ↔ LTPF',
  membres_business: 'Membres (activité identifiée)',
  membres_nommes: 'Autres membres nommés',
  membres_nommes_autres: 'Autres membres nommés',
};

const SKIP_KEYS = new Set(['label', 'intent', 'note', 'group_meta']);

const sectionLabel = (key: string): string =>
  SECTION_LABELS[key] ?? key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const isTargetArray = (v: unknown): v is NetworkTarget[] =>
  Array.isArray(v) && v.every((x) => x != null && typeof x === 'object');
const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string');

function contactIcon(c: string) {
  if (c.includes('@')) return Mail;
  if (/[+\d][\d\s().-]{6,}/.test(c)) return Phone;
  return null;
}

function matchTarget(t: NetworkTarget, q: string): boolean {
  if (!q) return true;
  return [t.name, t.note, t.activite, t.role, t.status, t.source, ...(t.contacts ?? [])]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(q));
}

// ─── Cellules ────────────────────────────────────────────────────────────────

function TargetRow({ t }: { t: NetworkTarget }) {
  const meta = [t.activite, t.value, t.role, t.status, t.source].filter(Boolean).join(' · ');
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border/40 py-1.5 last:border-0">
      <span className="font-medium">{t.name}</span>
      {t.priority === 'high' && (
        <Badge variant="warning" className="gap-1"><Star className="h-3 w-3" />prioritaire</Badge>
      )}
      {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
      {t.contacts?.map((c) => {
        const Icon = contactIcon(c);
        return (
          <span key={c} className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
            {Icon && <Icon className="h-3 w-3" />}{c}
          </span>
        );
      })}
      {t.note && <span className="text-xs italic text-muted-foreground">— {t.note}</span>}
    </div>
  );
}

function BucketCard({ bucket, query }: { bucket: NetworkBucket; query: string }) {
  const sections: Array<{ key: string; targets?: NetworkTarget[]; chips?: string[] }> = [];
  for (const [k, v] of Object.entries(bucket)) {
    if (SKIP_KEYS.has(k)) continue;
    if (isTargetArray(v)) sections.push({ key: k, targets: v.filter((t) => matchTarget(t, query)) });
    else if (isStringArray(v)) sections.push({ key: k, chips: v.filter((s) => !query || s.toLowerCase().includes(query)) });
  }
  const hasContent = sections.some((s) => (s.targets?.length ?? 0) > 0 || (s.chips?.length ?? 0) > 0);
  if (query && !hasContent) return null;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div>
          <h3 className="font-semibold">{bucket.label}</h3>
          {bucket.intent && <p className="mt-0.5 text-xs text-muted-foreground">{bucket.intent}</p>}
        </div>
        {sections.map((s) => {
          if (s.targets && s.targets.length > 0) {
            return (
              <div key={s.key}>
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{sectionLabel(s.key)}</div>
                <div>{s.targets.map((t, i) => <TargetRow key={`${t.name}-${i}`} t={t} />)}</div>
              </div>
            );
          }
          if (s.chips && s.chips.length > 0) {
            return (
              <div key={s.key}>
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{sectionLabel(s.key)}</div>
                <div className="flex flex-wrap gap-1.5">
                  {s.chips.map((c) => <span key={c} className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">{c}</span>)}
                </div>
              </div>
            );
          }
          return null;
        })}
        {bucket.note && <p className="text-[11px] italic text-muted-foreground">{bucket.note}</p>}
      </CardContent>
    </Card>
  );
}

function Kpi({ icon: Icon, label, value, tone }: { icon: typeof Users; label: string; value: string; tone?: 'warning' }) {
  const toneCls = tone === 'warning' ? 'text-warning' : 'text-info';
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className={cn('shrink-0', toneCls)}><Icon className="h-6 w-6" /></div>
        <div className="min-w-0">
          <div className={cn('num text-2xl font-bold leading-none', toneCls)}>{value}</div>
          <div className="mt-1 truncate text-xs text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Vue ─────────────────────────────────────────────────────────────────────

export function Network() {
  const { data, isLoading } = useNetwork();
  const [q, setQ] = useState('');
  const [bucketKey, setBucketKey] = useState('all');

  const bucketEntries = useMemo(() => Object.entries(data?.buckets ?? {}), [data]);
  const query = q.toLowerCase().trim();

  const stats = useMemo(() => {
    let named = 0;
    let high = 0;
    for (const [, b] of bucketEntries) {
      for (const [k, v] of Object.entries(b)) {
        if (SKIP_KEYS.has(k)) continue;
        if (isTargetArray(v)) {
          named += v.length;
          high += v.filter((t) => t.priority === 'high').length;
        }
      }
    }
    return { buckets: bucketEntries.length, named, high };
  }, [bucketEntries]);

  if (isLoading) return <Skeleton className="h-96" />;

  const visibleBuckets = bucketEntries.filter(([key]) => bucketKey === 'all' || key === bucketKey);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Kpi icon={NetworkIcon} label="Cercles / groupes" value={`${stats.buckets}`} />
        <Kpi icon={Users} label="Contacts nommés" value={`${stats.named}`} />
        <Kpi icon={Star} label="Cibles prioritaires" value={`${stats.high}`} tone="warning" />
      </div>

      {data?._meta && (
        <div className="flex items-start gap-2 rounded-md border border-info/30 bg-info/10 px-3 py-2 text-xs text-info">
          <NetworkIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {data._meta.title ?? 'Cartographie réseau'}
            {data._meta.generated && ` · généré le ${data._meta.generated}`}.
            {data._meta.sources && <> Sources : {data._meta.sources.join(' · ')}.</>}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer par nom, contact, activité…" className="pl-8" />
        </div>
        <Select value={bucketKey} onValueChange={setBucketKey}>
          <SelectTrigger className="h-9 w-56 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les cercles</SelectItem>
            {bucketEntries.map(([key, b]) => <SelectItem key={key} value={key}>{b.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {visibleBuckets.map(([key, b]) => <BucketCard key={key} bucket={b} query={query} />)}

      <p className="text-xs text-muted-foreground">
        Carte en lecture seule générée par MAESTRO. Pour l'enrichir : éditer <code>data/network-map.json</code>.
      </p>
    </div>
  );
}
